import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, inr, adminSession, restaurantSession } from "@/lib/api";
import { BrandLogo } from "@/components/tabdine/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";

const LoginView = ({ onDone }) => {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const login = async () => {
    try {
      const r = await api.post("/admin/login", { username: u, password: p });
      adminSession.set(r.data.token);
      toast.success("Welcome, admin");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed");
    }
  };
  return (
    <div className="min-h-screen bg-brand-dark text-brand-light grid place-items-center p-6">
      <div className="max-w-sm w-full">
        <div className="text-center mb-6">
          <div className="font-heading font-bold text-2xl">
            <span className="text-brand-light">tab</span><span className="text-brand-orange">dine</span>
          </div>
          <div className="text-xs uppercase tracking-widest text-brand-light/60 mt-1">Super Admin</div>
        </div>
        <div className="space-y-3">
          <Input placeholder="Username" value={u} onChange={(e) => setU(e.target.value)} className="bg-white text-brand-dark" data-testid="admin-user" />
          <Input type="password" placeholder="Password" value={p} onChange={(e) => setP(e.target.value)} className="bg-white text-brand-dark" data-testid="admin-pass" />
          <Button onClick={login} className="w-full bg-brand-orange text-white" data-testid="admin-login">Enter</Button>
        </div>
      </div>
    </div>
  );
};

export default function AdminPanel() {
  const [authed, setAuthed] = useState(!!adminSession.get());
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [rests, setRests] = useState([]);
  const [custs, setCusts] = useState([]);
  const [demos, setDemos] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [editingR, setEditingR] = useState(null);
  const [editF, setEditF] = useState({});
  const nav = useNavigate();

  const loadAll = () => {
    api.get("/admin/dashboard").then((r) => setDash(r.data));
    api.get("/admin/restaurants").then((r) => setRests(r.data));
    api.get("/admin/customers").then((r) => setCusts(r.data));
    api.get("/admin/demo-bookings").then((r) => setDemos(r.data));
    api.get("/admin/flagged-reviews").then((r) => setFlagged(r.data));
  };
  useEffect(() => {
    if (authed) loadAll();
  }, [authed]);

  const setDemoStatus = async (b, status) => {
    await api.patch(`/admin/demo-bookings/${b.id}`, { status });
    loadAll();
  };
  const deleteReview = async (id) => {
    if (!confirm("Permanently delete review?")) return;
    await api.delete(`/admin/reviews/${id}`);
    loadAll();
  };
  const toggleSuspend = async (r) => {
    await api.patch(`/admin/restaurants/${r.id}`, { is_suspended: !r.is_suspended });
    toast.success(r.is_suspended ? "Restaurant reactivated" : "Restaurant suspended");
    loadAll();
  };
  const changePlan = async (r, plan) => {
    await api.patch(`/admin/restaurants/${r.id}`, { subscription_plan: plan });
    toast.success(`${r.name} → ${plan}`);
    loadAll();
  };
  const changeExpiry = async (r, dateStr) => {
    await api.patch(`/admin/restaurants/${r.id}`, { subscription_expires_at: dateStr ? new Date(dateStr).toISOString() : null });
    loadAll();
  };
  const deleteRestaurant = async (r) => {
    if (!confirm(`Permanently delete "${r.name}"? This deletes all its menu, orders, reviews, and tables too. This cannot be undone.`)) return;
    if (prompt(`Type the restaurant's name to confirm deletion:`) !== r.name) {
      toast.error("Name didn't match — deletion cancelled");
      return;
    }
    try {
      await api.delete(`/admin/restaurants/${r.id}`);
      toast.success(`${r.name} deleted`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };
  const impersonate = async (r) => {
    try {
      const res = await api.post(`/admin/restaurants/${r.id}/impersonate`);
      restaurantSession.set(res.data.token, res.data.restaurant);
      toast.success(`Signed in as ${r.name}`);
      nav("/restaurant/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impersonation failed");
    }
  };
  const openEdit = (r) => {
    setEditingR(r);
    setEditF({ name: r.name, city: r.city || "", address: r.address || "" });
  };
  const saveEdit = async () => {
    await api.patch(`/admin/restaurants/${editingR.id}`, editF);
    toast.success("Updated");
    setEditingR(null);
    loadAll();
  };

  if (!authed) return <LoginView onDone={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-brand-dark text-brand-light font-body">
      <header className="border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="font-heading font-bold text-xl">
            <span className="text-brand-light">tab</span><span className="text-brand-orange">dine</span>
          </div>
          <span className="text-xs uppercase tracking-widest text-brand-light/50">Admin</span>
        </div>
        <button onClick={() => { adminSession.clear(); setAuthed(false); }} className="text-xs text-brand-light/60 hover:text-brand-light" data-testid="admin-logout">Sign out</button>
      </header>

      <div className="flex gap-1 border-b border-white/10 px-4 overflow-x-auto hide-scrollbar">
        {[
          ["dashboard", "Dashboard"], ["restaurants", "Restaurants"],
          ["customers", "Customers"], ["demos", "Leads"], ["reviews", "Flagged Reviews"],
          ["settings", "Settings"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`admin-tab-${k}`}
            className={`px-4 py-2 text-sm ${tab === k ? "text-brand-orange border-b-2 border-brand-orange" : "text-brand-light/60"}`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="p-4 lg:p-6">
        {tab === "dashboard" && dash && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KPI l="Restaurants" v={dash.total_restaurants} />
            <KPI l="Active subs" v={dash.active_subscriptions} />
            <KPI l="Today orders" v={dash.today_orders} />
            <KPI l="Today revenue" v={inr(dash.today_revenue)} />
            <KPI l="Today taps" v={dash.today_taps} />
            <KPI l="Customers" v={dash.total_customers} />
          </div>
        )}

        {tab === "restaurants" && (
          <div className="bg-white/5 rounded-xl border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-xs uppercase tracking-widest text-brand-light/50">
                  <th className="text-left p-3">Restaurant</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Plan</th>
                  <th className="text-left p-3">Joined</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rests.map((r) => (
                  <tr key={r.id} className="border-b border-white/10" data-testid={`admin-r-${r.slug}`}>
                    <td className="p-3">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-xs text-brand-light/50">/{r.slug} · {r.contact_name || "—"} · {r.contact_phone || "—"}</div>
                    </td>
                    <td className="p-3">
                      <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${r.is_suspended ? "bg-red-500/20 text-red-300" : "bg-green-500/20 text-green-300"}`}>
                        {r.is_suspended ? "Suspended" : "Active"}
                      </span>
                    </td>
                    <td className="p-3">
                      <select
                        value={r.subscription_plan || "trial"}
                        onChange={(e) => changePlan(r, e.target.value)}
                        data-testid={`plan-${r.slug}`}
                        className="text-xs bg-white/10 border border-white/10 rounded px-2 py-1 text-brand-light"
                      >
                        <option value="free">Free</option>
                        <option value="trial">Trial</option>
                        <option value="basic">Basic</option>
                        <option value="pro">Pro</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                      <input
                        type="date"
                        defaultValue={r.subscription_expires_at ? r.subscription_expires_at.slice(0, 10) : ""}
                        onBlur={(e) => changeExpiry(r, e.target.value)}
                        data-testid={`plan-expiry-${r.slug}`}
                        title="Subscription expiry date"
                        className="block mt-1 text-[10px] bg-white/10 border border-white/10 rounded px-2 py-1 text-brand-light/80"
                      />
                    </td>
                    <td className="p-3 text-xs text-brand-light/60">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end flex-wrap">
                        <button onClick={() => openEdit(r)} data-testid={`edit-${r.slug}`} className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20">Edit</button>
                        <button onClick={() => impersonate(r)} data-testid={`imp-${r.slug}`} className="text-xs px-2 py-1 rounded bg-brand-orange/80 hover:bg-brand-orange text-white">Login as</button>
                        <button
                          onClick={() => toggleSuspend(r)}
                          data-testid={`susp-${r.slug}`}
                          className={`text-xs px-2 py-1 rounded ${r.is_suspended ? "bg-green-500/80 hover:bg-green-500 text-white" : "bg-red-500/80 hover:bg-red-500 text-white"}`}
                        >
                          {r.is_suspended ? "Unsuspend" : "Suspend"}
                        </button>
                        <button
                          onClick={() => deleteRestaurant(r)}
                          data-testid={`del-${r.slug}`}
                          className="text-xs px-2 py-1 rounded bg-red-700/80 hover:bg-red-700 text-white"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "customers" && (
          <div className="bg-white/5 rounded-xl border border-white/10 divide-y divide-white/10">
            {custs.map((c) => (
              <div key={c.id} className="p-3 text-sm flex justify-between">
                <div>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-brand-light/60">{c.phone}</div>
                </div>
                <div className="text-xs text-brand-light/60">{(c.created_at || "").slice(0, 10)}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "demos" && (
          <div className="bg-white/5 rounded-xl border border-white/10 divide-y divide-white/10">
            {demos.length === 0 && <div className="p-4 text-brand-light/60 text-sm">No leads yet.</div>}
            {demos.map((b) => (
              <div key={b.id} className="p-3 text-sm flex items-center justify-between gap-2" data-testid={`admin-demo-${b.id}`}>
                <div>
                  <div className="font-medium">{b.contact_name} — {b.restaurant_name}</div>
                  <div className="text-xs text-brand-light/60">{b.phone} · {b.email} · {b.city}</div>
                  <div className="text-xs text-brand-light/50">{b.preferred_date} · {b.preferred_time_slot}</div>
                </div>
                <select value={b.status} onChange={(e) => setDemoStatus(b, e.target.value)} className="text-xs bg-white/10 border border-white/10 rounded px-2 py-1 text-brand-light">
                  <option value="pending">pending</option>
                  <option value="confirmed">confirmed</option>
                  <option value="completed">completed</option>
                  <option value="no_show">no_show</option>
                  <option value="cancelled">cancelled</option>
                </select>
              </div>
            ))}
          </div>
        )}

        {tab === "settings" && <AdminSettingsPanel />}

        {tab === "reviews" && (
          <div className="bg-white/5 rounded-xl border border-white/10 divide-y divide-white/10">
            {flagged.length === 0 && <div className="p-4 text-brand-light/60 text-sm">No flagged reviews.</div>}
            {flagged.map((rv) => (
              <div key={rv.id} className="p-3 text-sm flex items-center justify-between">
                <div>
                  <div className="font-medium">★ {rv.rating} — {rv.customer_name}</div>
                  <div className="text-xs text-brand-light/70 mt-0.5">{rv.comment}</div>
                </div>
                <Button size="sm" variant="destructive" onClick={() => deleteReview(rv.id)} data-testid={`admin-del-${rv.id}`}>Delete</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editingR} onOpenChange={(v) => !v && setEditingR(null)}>
        <DialogContent data-testid="edit-restaurant-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editingR?.name}</DialogTitle>
            <DialogDescription>Update the restaurant's core profile.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-brand-dark/60">Name</label>
              <Input value={editF.name || ""} onChange={(e) => setEditF({ ...editF, name: e.target.value })} data-testid="edit-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-brand-dark/60">City</label>
                <Input value={editF.city || ""} onChange={(e) => setEditF({ ...editF, city: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-brand-dark/60">Address</label>
                <Input value={editF.address || ""} onChange={(e) => setEditF({ ...editF, address: e.target.value })} />
              </div>
            </div>
            <Button onClick={saveEdit} className="w-full bg-brand-orange text-white" data-testid="save-edit">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const AdminSettingsPanel = () => {
  const [current, setCurrent] = useState("");
  const [newUser, setNewUser] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!current || !newPass) {
      toast.error("Enter your current password and a new password");
      return;
    }
    if (newPass !== confirmPass) {
      toast.error("New passwords don't match");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/admin/change-password", {
        current_password: current,
        new_username: newUser || undefined,
        new_password: newPass,
      });
      toast.success(`Credentials updated. New username: ${res.data.username}`);
      setCurrent(""); setNewUser(""); setNewPass(""); setConfirmPass("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update credentials");
    }
    setBusy(false);
  };

  return (
    <div className="max-w-md bg-white/5 rounded-xl border border-white/10 p-5 space-y-3">
      <div className="font-heading font-semibold text-lg">Change admin login</div>
      <div className="text-xs text-brand-light/60">
        Leave username blank to keep it the same. New password must be at least 6 characters.
      </div>
      <div>
        <label className="text-xs text-brand-light/60">Current password</label>
        <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="admin-settings-current" className="bg-white/10 text-brand-light border-white/10" />
      </div>
      <div>
        <label className="text-xs text-brand-light/60">New username (optional)</label>
        <Input value={newUser} onChange={(e) => setNewUser(e.target.value)} data-testid="admin-settings-new-user" className="bg-white/10 text-brand-light border-white/10" />
      </div>
      <div>
        <label className="text-xs text-brand-light/60">New password</label>
        <Input type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} data-testid="admin-settings-new-pass" className="bg-white/10 text-brand-light border-white/10" />
      </div>
      <div>
        <label className="text-xs text-brand-light/60">Confirm new password</label>
        <Input type="password" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} data-testid="admin-settings-confirm-pass" className="bg-white/10 text-brand-light border-white/10" />
      </div>
      <Button onClick={submit} disabled={busy} className="w-full bg-brand-orange text-white" data-testid="admin-settings-save">
        {busy ? "Saving…" : "Update credentials"}
      </Button>
    </div>
  );
};

const KPI = ({ l, v }) => (
  <div className="bg-white/5 rounded-xl border border-white/10 p-4">
    <div className="text-xs uppercase tracking-widest text-brand-light/50">{l}</div>
    <div className="font-heading text-2xl font-bold mt-1">{v}</div>
  </div>
);
